/**
 * Official TMHP form text shown read-only in the PA forms (Phase 8 payer forms).
 *
 * Word-for-word copy of the official PDFs from https://www.tmhp.com/resources/forms (pa-forms),
 * downloaded 2026-10-08 (F00012 rev. 06/07/2023, F00028 rev. 06/07/2023, F00120 rev. 08/04/2021,
 * F00070 rev. 08/21/2021). Mirrors blackbird_backend .../patientmr/pdf/tmhpFormText.js, which the
 * PDFs and the server-side gates use; keep both in step and never reword. See
 * docs/audits/payer_forms_matrix.md.
 */

export interface TmhpStatement {
  key: string;
  text: string;
}

export const SUBMITTER_CERTIFICATION_TITLE = "Prior Authorization Request Submitter Certification Statement";
export const WE_AGREE_LABEL = "We Agree";

export type SubmitterCertificationForm = 'F00012' | 'F00028' | 'F00120' | 'F00070';

const SUBMITTER_PARAGRAPHS_DEFAULT: string[] = [
  "I certify and affirm that I am either the Provider, or have been specifically authorized by the Provider (hereinafter \"Prior Authorization Request Submitter\") to submit this prior authorization request.",
  "The Provider and Prior Authorization Request Submitter certify and affirm under penalty of perjury that they are personally acquainted with the information supplied on the prior authorization form and any attachments or accompanying information and that it constitutes true, correct, complete and accurate information; does not contain any misrepresentations; and does not fail to include any information that might be deemed relevant or pertinent to the decision on which a prior authorization for payment would be made.",
  "The Provider and Prior Authorization Request Submitter certify and affirm under penalty of perjury that the information supplied on the prior authorization form and any attachments or accompanying information was made by a person with knowledge of the act, event, condition, opinion, or diagnosis recorded; is kept in the ordinary course of business of the Provider; is the original or an exact duplicate of the original; and is maintained in the individual patient's medical record in accordance with the Texas Medicaid Provider Procedures Manual (TMPPM).",
  "The Provider and Prior Authorization Request Submitter certify and affirm that they understand and agree that prior authorization is a condition of reimbursement and is not a guarantee of payment.",
  "The Provider and Prior Authorization Request Submitter understand that payment of claims related to this prior authorization will be from Federal and State funds, and that any false claims, statements or documents, concealment of a material fact, or omitting relevant or pertinent information may constitute fraud and may be prosecuted under applicable federal and/or State laws. The Provider and Prior Authorization Request Submitter understand and agree that failure to provide true and accurate information, omit information, or provide notice of changes to the information previously provided may result in termination of the provider's Medicaid enrollment and/or personal exclusion from Texas Medicaid.",
  "The Provider and Prior Authorization Request Submitter certify, affirm and agree that by checking \"We Agree\" that they have read and understand the Prior Authorization Agreement requirements as stated in the relevant Texas Medicaid Provider Procedures Manual and they agree and consent to the Certification above and to the Texas Medicaid & Healthcare Partnership (TMHP) Terms and Conditions."
];

/** F00120 prints a comma after "accompanying information" in paragraph 2. */
const SUBMITTER_PARAGRAPHS_F00120: string[] = [
  "I certify and affirm that I am either the Provider, or have been specifically authorized by the Provider (hereinafter \"Prior Authorization Request Submitter\") to submit this prior authorization request.",
  "The Provider and Prior Authorization Request Submitter certify and affirm under penalty of perjury that they are personally acquainted with the information supplied on the prior authorization form and any attachments or accompanying information, and that it constitutes true, correct, complete and accurate information; does not contain any misrepresentations; and does not fail to include any information that might be deemed relevant or pertinent to the decision on which a prior authorization for payment would be made.",
  "The Provider and Prior Authorization Request Submitter certify and affirm under penalty of perjury that the information supplied on the prior authorization form and any attachments or accompanying information was made by a person with knowledge of the act, event, condition, opinion, or diagnosis recorded; is kept in the ordinary course of business of the Provider; is the original or an exact duplicate of the original; and is maintained in the individual patient's medical record in accordance with the Texas Medicaid Provider Procedures Manual (TMPPM).",
  "The Provider and Prior Authorization Request Submitter certify and affirm that they understand and agree that prior authorization is a condition of reimbursement and is not a guarantee of payment.",
  "The Provider and Prior Authorization Request Submitter understand that payment of claims related to this prior authorization will be from Federal and State funds, and that any false claims, statements or documents, concealment of a material fact, or omitting relevant or pertinent information may constitute fraud and may be prosecuted under applicable federal and/or State laws. The Provider and Prior Authorization Request Submitter understand and agree that failure to provide true and accurate information, omit information, or provide notice of changes to the information previously provided may result in termination of the provider's Medicaid enrollment and/or personal exclusion from Texas Medicaid.",
  "The Provider and Prior Authorization Request Submitter certify, affirm and agree that by checking \"We Agree\" that they have read and understand the Prior Authorization Agreement requirements as stated in the relevant Texas Medicaid Provider Procedures Manual and they agree and consent to the Certification above and to the Texas Medicaid & Healthcare Partnership (TMHP) Terms and Conditions."
];

export function submitterCertificationParagraphs(formCode: SubmitterCertificationForm): string[] {
  return formCode === 'F00120' ? SUBMITTER_PARAGRAPHS_F00120 : SUBMITTER_PARAGRAPHS_DEFAULT;
}

// ─── F00120 ──────────────────────────────────────────────────────────────────

export const F00120_SERVICE_PROMPT = "Please check the appropriate box:";
/** Stored serviceType: PDN box, PPECC box, or both boxes. */
export type F00120ServiceType = 'PDN' | 'PPECC' | 'BOTH';
export const F00120_INITIALS_LABEL = "Client/Responsible Adult Initials:";
export const F00120_ACK_TITLE = "5. Acknowledgements";
export const F00120_ACK_INSTRUCTION = "Must be signed by the client/responsible adult, the skilled nursing provider(s) (PDN and/or PPECC) and the prescribing physician.";
export const F00120_ACK_INTRO = "By signing this form, the client/responsible adult, the skilled nursing provider (PDN and/or PPECC) and the prescribing physician acknowledge:";
export const F00120_ACKNOWLEDGEMENTS: TmhpStatement[] = [
  {
    key: "responsibleAdultContingency",
    text: "Clients under 18 years of age reside with an identified responsible adult/parent/guardian who is either trained to provide nursing care or is capable of initiating an identified contingency plan when scheduled PDN or PPECC services are unexpectedly unavailable;"
  },
  {
    key: "writtenConsentTreatment",
    text: "The client/responsible adult has provided written consent to the treatment;"
  },
  {
    key: "contingencyDischargePlans",
    text: "The client has identified contingency and discharge plans;"
  },
  {
    key: "primaryPhysician",
    text: "The client has a primary physician who provides ongoing health care and medical supervision;"
  },
  {
    key: "placeOfServiceSafe",
    text: "The place(s) where PDN and/or PPECC services will be delivered supports the health and safety of the client;"
  },
  {
    key: "backupSystems",
    text: "If applicable, there are necessary backup utilities, communication, fire and safety systems available and functional;"
  },
  {
    key: "consentShareHealthInfo",
    text: "The client's consent to share personal health information with other health care providers, as needed to ensure coordination of care;"
  },
  {
    key: "skilledNursingInfo",
    text: "Discussion and receipt of information about skilled nursing (PDN and/or PPECC) services;"
  },
  {
    key: "notForRespite",
    text: "PDN and/or PPECC services are not authorized for respite, child care, activities of daily living or housekeeping;"
  },
  {
    key: "nursingCarePlanParticipation",
    text: "Participation in the development of the Nursing Care Plan for this client;"
  },
  {
    key: "emergencyPlans",
    text: "Emergency plans are part of the client's care plan and include telephone numbers for the client's physician, ambulance, hospital, and equipment supplier and information on how to handle emergency situations;"
  },
  {
    key: "followPlanOfCare",
    text: "The client/responsible adult agrees to follow through with the plan of care as prescribed by the client's physician; and"
  },
  {
    key: "criteriaMetDocumentation",
    text: "All required criteria are met and completed documentation is submitted to TMHP."
  }
];
export const F00120_COORDINATION_TITLE = "Acknowledgement of Coordination of Approved Skilled Nursing Hours";
export const F00120_COORDINATION_INTRO = "By signing this form, the client/responsible adult, the prescribing physician, the PDN provider and the PPECC provider acknowledge:";
export const F00120_COORDINATION: TmhpStatement[] = [
  {
    key: "bothSkilledNursing",
    text: "The client/responsible adult understands that PDN and PPECC services are both considered skilled nursing services;"
  },
  {
    key: "setNumberOfHours",
    text: "Skilled nursing services are authorized for a set number of hours based on the client's medical necessity at the time of the prior authorization request;"
  },
  {
    key: "consentNoIncrease",
    text: "The client/responsible adult has provided written consent, including acknowledgement, that subsequent approval of either PDN or PPECC services will not increase the number of approved skilled nursing hours unless there is a documented change in the client's medical condition, or the authorized hours are not commensurate to the client's medical needs and additional hours are medically necessary;"
  },
  {
    key: "bothProvidersFlowSheet",
    text: "When PDN and PPECC providers are both authorized to provide skilled nursing tasks, the services will be provided by both providers as documented in the \"Schedule of Services 24-hour Daily Flow Sheet\";"
  },
  {
    key: "consentHoursReduced",
    text: "The client/responsible adult has provided written consent, including acknowledgement, that upon subsequent approval of PDN or PPECC services the provider who submitted the initial prior authorization request that established the number of authorized skilled nursing hours will have their authorized hours reduced; and"
  },
  {
    key: "noIncreaseWithoutRevision",
    text: "The client/responsible adult, the prescribing physician, the PDN provider and the PPECC provider acknowledge the authorized number of skilled nursing hours will not increase unless a revised prior authorization request is submitted to TMHP with documentation that supports an increase in skilled nursing hours (a change in the client's medical condition or authorized hours are not commensurate to the client's medical needs)."
  }
];

// ─── F00012 Section F ────────────────────────────────────────────────────────

export type F00012SectionFProgram = 'ABA' | 'DME_SUPPLIES' | 'PDN' | 'PPECC';
export const F00012_SECTION_F_TITLE = "F: Primary Practitioner's Certifications (To be completed by the requesting physician or allowed practitioner)";
export const F00012_SECTION_F_CERTIFICATIONS: Record<F00012SectionFProgram, { heading: string; statements: string[] }> = {
  ABA: {
    heading: "By requesting ABA evaluation or treatment, I certify:",
    statements: [
      "The client is under 21 years of age AND",
      "The client has a diagnosis of Autism Spectrum Disorder AND",
      "ABA services are or may be clinically indicated"
    ]
  },
  DME_SUPPLIES: {
    heading: "By requesting the identified DME and/or medical supplies, I certify:",
    statements: [
      "The client is under 21 years of age AND",
      "The prescribed items are appropriate and can safely be used by the client when used as prescribed"
    ]
  },
  PDN: {
    heading: "By requesting Private Duty Nursing, I certify:",
    statements: [
      "The client is under 21 years of age AND",
      "The client's medical condition is sufficiently stable to permit safe delivery of private duty nursing as described in the plan of care."
    ]
  },
  PPECC: {
    heading: "By requesting PPECC services, I certify:",
    statements: [
      "The client is under 21 years of age AND",
      "The client's medical condition is sufficiently stable to permit safe delivery of PPECC services as described in the PPECC plan of care."
    ]
  }
};
export const F00012_SECTION_F_PROGRAMS: F00012SectionFProgram[] = ['ABA', 'DME_SUPPLIES', 'PDN', 'PPECC'];
export const F00012_SECTION_F_NOTE = "Note: Signatures from chiropractors and doctors of philosophy (PhDs) will not be accepted. Certified Nurse Midwife (CNM), Clinical Nurse Specialist (CNS), Nurse Practitioner (NP) and Physician Assistant (PA) providers may sign on behalf of the physician for Applied Behavior Analysis (ABA) services, Private Duty Nursing, Physical, Occupational, and Speech Therapy Services when the physician delegates this authority. Signature stamps and date stamps are not acceptable.";

/** Programs whose Section F certification applies, from the "Request for:" boxes. */
export function f00012SectionFPrograms(data: Record<string, unknown>): F00012SectionFProgram[] {
  const programs: F00012SectionFProgram[] = [];
  if (data.requestTypeABA) programs.push('ABA');
  if (data.requestTypeDME || data.requestTypeSupplies) programs.push('DME_SUPPLIES');
  if (data.requestTypePDN) programs.push('PDN');
  if (data.requestTypePPECC) programs.push('PPECC');
  return programs;
}

// ─── F00070 ──────────────────────────────────────────────────────────────────

/** Instruction printed after the six criteria (not a criterion, O-6). */
export const F00070_ACK_INSTRUCTION = "All required acknowledgments must be signed and dated.";

// ─── F00012 Section E ────────────────────────────────────────────────────────

export type F00012HcpcsKey = 'code' | 'modifier' | 'description' | 'quantity' | 'frequency' | 'retailPrice';
/**
 * Requested-services columns. The official form heads them "HCPCS Code* / Modifier",
 * "Brief Description of Requested Services", "Quantity* / Frequency*", "Retail Price";
 * each "/" pair is two columns here. `key` is the field in `hcpcsRows[]`.
 */
export const F00012_HCPCS_COLUMNS: { key: F00012HcpcsKey; label: string }[] = [
  {
    "key": "code",
    "label": "HCPCS Code*"
  },
  {
    "key": "modifier",
    "label": "Modifier"
  },
  {
    "key": "description",
    "label": "Brief Description of Requested Services"
  },
  {
    "key": "quantity",
    "label": "Quantity*"
  },
  {
    "key": "frequency",
    "label": "Frequency*"
  },
  {
    "key": "retailPrice",
    "label": "Retail Price"
  }
];
export const F00012_HCPCS_NOTE = "Note: HCPCS codes and descriptions must be provided.";

// ─── F00028 Section C (PPECC) ────────────────────────────────────────────────

export const F00028_PPECC_HOURS_LABEL = "PPECC Hours of Operation:";
export type F00028PpeccTimeZone = 'Central' | 'Mountain';
/** Stored `ppeccTimezone` values and the official check-box labels. */
export const F00028_PPECC_TIME_ZONES: { value: F00028PpeccTimeZone; label: string }[] = [
  {
    "value": "Central",
    "label": "Central Time"
  },
  {
    "value": "Mountain",
    "label": "Mountain Time"
  }
];

/** Agency IANA time zone → F00028 PPECC time box ('' when it is neither Central nor Mountain). */
export function ppeccTimeZoneFromAgency(agencyTimezone: unknown): F00028PpeccTimeZone | '' {
  if (agencyTimezone === 'America/Chicago') return 'Central';
  if (agencyTimezone === 'America/Denver') return 'Mountain';
  return '';
}

// ─── F00120 page 2 abbreviations ─────────────────────────────────────────────

export const F00120_ABBREVIATIONS_INTRO = "Use the following abbreviations to identify services provided on the 24-hour Daily Flow Sheet (see pages 5 – 10).";
/** Official abbreviation list (F00120 page 2), in form order. */
export const F00120_ABBREVIATIONS: { abbreviation: string; description: string }[] = [
  {
    "abbreviation": "AFO",
    "description": "Application of ankle foot orthotics"
  },
  {
    "abbreviation": "BGM",
    "description": "Blood glucose monitor"
  },
  {
    "abbreviation": "Bi PAP",
    "description": "Bi-level positive airway pressure"
  },
  {
    "abbreviation": "BP",
    "description": "Blood pressure"
  },
  {
    "abbreviation": "CPAP",
    "description": "Continuous positive airway pressure"
  },
  {
    "abbreviation": "CPT",
    "description": "Chest percussion therapy"
  },
  {
    "abbreviation": "Dx",
    "description": "Diagnoses"
  },
  {
    "abbreviation": "GI Assess",
    "description": "Assessment of the GI tract/functions"
  },
  {
    "abbreviation": "GT/GB",
    "description": "Gastrostomy tube/ gastrostomy button"
  },
  {
    "abbreviation": "GTF/ GBF",
    "description": "Gastrostomy tube feeding/ gastrostomy button feeding"
  },
  {
    "abbreviation": "GU Assess",
    "description": "Assessment of the genitourinary system"
  },
  {
    "abbreviation": "I&O",
    "description": "Intake and output"
  },
  {
    "abbreviation": "I & O cath",
    "description": "In and out urinary catheterization"
  },
  {
    "abbreviation": "IM",
    "description": "Intramuscular injection"
  },
  {
    "abbreviation": "Incont Care",
    "description": "Care of incontinent episodes (skin care)"
  },
  {
    "abbreviation": "IPPB",
    "description": "Intermittent positive pressure breathing"
  },
  {
    "abbreviation": "IPPV",
    "description": "Intermittent positive pressure ventilation"
  },
  {
    "abbreviation": "IV/ IVF",
    "description": "Intravenous/ fluids or medications"
  },
  {
    "abbreviation": "Med/Meds",
    "description": "Medication given"
  },
  {
    "abbreviation": "Neb TX",
    "description": "Nebulizer/ aerosol treatment"
  },
  {
    "abbreviation": "Neuro Assess",
    "description": "Neurological assessment"
  },
  {
    "abbreviation": "NGT",
    "description": "Nasogastric tube"
  },
  {
    "abbreviation": "NGTF",
    "description": "Nasogastric tube feeding"
  },
  {
    "abbreviation": "O2",
    "description": "Oxygen"
  },
  {
    "abbreviation": "O2 Sats",
    "description": "Oxygen saturation level"
  },
  {
    "abbreviation": "PAC",
    "description": "Port a cath IV access"
  },
  {
    "abbreviation": "PDA",
    "description": "Private duty aide"
  },
  {
    "abbreviation": "PDN",
    "description": "Private duty nursing by registered nurse (RN) or licensed vocational nurse (LVN)"
  },
  {
    "abbreviation": "Phys Assess",
    "description": "Physical assessment/total body assessment—including head-to-toe review of body systems"
  },
  {
    "abbreviation": "PPECC",
    "description": "Prescribed Pediatric Extended Care Center"
  },
  {
    "abbreviation": "Prec",
    "description": "Precautions"
  },
  {
    "abbreviation": "PRN",
    "description": "As needed"
  },
  {
    "abbreviation": "Resp Assess",
    "description": "Respiratory assessment"
  },
  {
    "abbreviation": "ROM",
    "description": "Range of motion"
  },
  {
    "abbreviation": "SHARS",
    "description": "School Health and Rehabilitative Services"
  },
  {
    "abbreviation": "SQ",
    "description": "Subcutaneous"
  },
  {
    "abbreviation": "SXN / SUX",
    "description": "Suctioning"
  },
  {
    "abbreviation": "Sz",
    "description": "Seizure"
  },
  {
    "abbreviation": "TPR",
    "description": "Temperature, pulse, respiration"
  },
  {
    "abbreviation": "Trach",
    "description": "Tracheostomy/tracheotomy"
  },
  {
    "abbreviation": "Vent",
    "description": "Ventilator"
  },
  {
    "abbreviation": "VS",
    "description": "Vital signs"
  }
];
